'use client';

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';

// Import countries data - using the same path as v1
import { countries } from '@/data/countries';

// ============================================================================
// Types & Schema
// ============================================================================

const applePayFormSchema = z.object({
  // Metadata (UI preferences)
  country: z.string().min(1, 'Country is required'),
  enableExpressCheckout: z.boolean(),
  enablePaymentFormCheckout: z.boolean(),
  // Preserved passthrough only — domain verification is no longer managed here
  // (it happens through the Apple Pay certificate/domain setup). Kept so an
  // existing store's already-verified domains aren't wiped on save.
  domains: z.record(z.object({ verified: z.boolean() })).optional(),
});

type FormValues = z.infer<typeof applePayFormSchema>;

interface IntegrationDefaultValues {
  enabled?: boolean;
  metadata?: {
    country?: string;
    enableExpressCheckout?: boolean;
    enablePaymentFormCheckout?: boolean;
    domains?: Record<string, { verified: boolean }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ApplePayFormProps {
  defaultValues?: IntegrationDefaultValues;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  isLoading?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ApplePayForm({ defaultValues = {}, onSubmit, isLoading = false }: ApplePayFormProps) {
  const { t } = useTranslation('integrations');

  const form = useForm<FormValues>({
    resolver: zodResolver(applePayFormSchema),
    mode: 'all',
    defaultValues: {
      country: (defaultValues?.metadata?.country as string) ?? 'US',
      enableExpressCheckout: (defaultValues?.metadata?.enableExpressCheckout as boolean) ?? true,
      enablePaymentFormCheckout: (defaultValues?.metadata?.enablePaymentFormCheckout as boolean) ?? false,
      domains: (defaultValues?.metadata?.domains as Record<string, { verified: boolean }>) ?? {},
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const handleFormSubmit = async (data: FormValues) => {
    const { country, enableExpressCheckout, enablePaymentFormCheckout, domains } = data;

    await onSubmit({
      metadata: {
        country,
        enableExpressCheckout,
        enablePaymentFormCheckout,
        // Preserve any existing verified domains untouched.
        domains,
      },
    });
  };

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Country Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('applePay.countryTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Select
            value={watch('country')}
            onValueChange={(value) => setValue('country', value)}
            disabled={isLoading}
          >
            <SelectTrigger id="country">
              <SelectValue placeholder={t('applePay.selectCountry')} />
            </SelectTrigger>
            <SelectContent>
              {countries.map((country) => (
                <SelectItem key={country['alpha-2']} value={country['alpha-2']}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.country && <p className="text-destructive text-xs">{errors.country.message}</p>}
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>{t('applePay.features')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enableExpressCheckout"
                checked={watch('enableExpressCheckout')}
                onCheckedChange={(checked) => setValue('enableExpressCheckout', checked as boolean)}
                disabled={isLoading}
              />
              <Label htmlFor="enableExpressCheckout">{t('applePay.enableExpressCheckout')}</Label>
            </div>
            <p className="text-muted-foreground pl-6 text-sm">{t('applePay.expressCheckoutHint')}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enablePaymentFormCheckout"
                checked={watch('enablePaymentFormCheckout')}
                onCheckedChange={(checked) => setValue('enablePaymentFormCheckout', checked as boolean)}
                disabled={isLoading}
              />
              <Label htmlFor="enablePaymentFormCheckout">{t('applePay.enablePaymentFormCheckout')}</Label>
            </div>
            <p className="text-muted-foreground pl-6 text-sm">{t('applePay.paymentFormCheckoutHint')}</p>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
